import React, { ReactNode } from 'react'
import Downshift, {
  ControllerStateAndHelpers,
  DownshiftProps,
  DownshiftState,
  StateChangeOptions,
} from 'downshift'

type DownshiftStates = Record<string, any>

interface MultiDownshiftProps {
  value?: string[]

  onChange?(selectedItems: string[], state: DownshiftStates): void

  onSelect?(selectedItems: string[], state: DownshiftStates): void

  externalSync?: boolean
  children?: ReactNode | ((value: any) => ReactNode)

  render?(state: Record<string, any>): ReactNode
}

interface MultiDownshiftStates {
  selectedItems: string[]
}

type Props = MultiDownshiftProps & DownshiftProps<{ name: string }>

class MultiDownshift extends React.Component<Props, MultiDownshiftStates> {
  override state = { selectedItems: this.props.value || [] }

  stateReducer = (
    state: DownshiftState<any>,
    changes: StateChangeOptions<any>,
  ) => {
    switch (changes.type) {
      case Downshift.stateChangeTypes.keyDownEnter:
      case Downshift.stateChangeTypes.clickItem:
        return {
          ...changes,
          highlightedIndex: state.highlightedIndex,
          isOpen: true,
        }
      default:
        return changes
    }
  }

  handleSelection = (
    selectedItem: string,
    downshift: ControllerStateAndHelpers<any>,
  ) => {
    const callOnChange = () => {
      if (this.props.onSelect) {
        this.props.onSelect(
          this.state.selectedItems,
          this.getStateAndHelpers(downshift),
        )
      }
      if (this.props.onChange) {
        this.props.onChange(
          this.state.selectedItems,
          this.getStateAndHelpers(downshift),
        )
      }
    }
    if (this.state.selectedItems.includes(selectedItem)) {
      this.removeItem(selectedItem, callOnChange)
    } else {
      this.addSelectedItem(selectedItem, callOnChange)
    }
  }

  removeItem = (item: string, cb: () => void) => {
    this.setState(({ selectedItems }) => {
      return {
        selectedItems: selectedItems.filter((i) => i !== item),
      }
    }, cb)
  }

  addSelectedItem(item: string, cb: () => void) {
    this.setState(
      ({ selectedItems }) => ({
        selectedItems: [...selectedItems, item],
      }),
      cb,
    )
  }

  getStateAndHelpers(downshift: ControllerStateAndHelpers<any>) {
    const { selectedItems } = this.state
    return {
      selectedItems,
      ...downshift,
    }
  }

  setSelectedItems(values: string[]) {
    this.setState({
      selectedItems: values,
    })
  }

  override render() {
    const { render, children = render, ...props } = this.props

    return (
      <Downshift
        {...props}
        stateReducer={this.stateReducer}
        onChange={this.handleSelection}
        selectedItem={null}
      >
        {(downshift) =>
          children && children(this.getStateAndHelpers(downshift))
        }
      </Downshift>
    )
  }
}

export default MultiDownshift
